import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { formatCurrency, num } from "../Invoices/InvoicesStore";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * The create form.
 *
 * `lineItems` is not optional decoration: `recurring_line_items` is the
 * TEMPLATE the schedule bills from, the API lifts it off a `lineItems` key on
 * create (`persistRecurringLineItems`), and no other surface in this app writes
 * that table — the service store exists but nothing reads or edits it. So a
 * schedule created without rows here can never acquire them, and the schedule
 * is inert by construction.
 *
 * That is a stronger reason than the one that nearly let this ship without
 * them. Nothing generates invoices from a schedule TODAY — `planScheduleRun`
 * is a pure, tested, uncalled module and `autoSend` has no consumer beyond the
 * column that declares it — so no empty invoice can currently be raised or
 * sent. But the generator is plainly coming (the arithmetic is already written
 * and tested), and the day it lands, every row this form created without a
 * template becomes a client invoice with no charges on it.
 */
export interface RecurringLineItemForm {
  description: string;
  quantity: string;
  unitPrice: string;
}

export interface RecurringForm {
  clientId: string;
  title: string;
  frequency: string;
  nextRunDate: string;
  taxRate: string;
  notes: string;
  autoSend: boolean;
  lineItems: RecurringLineItemForm[];
}

/** Mirrors `recurringFrequencies` in the API schema. */
export const RECURRING_FREQUENCIES = [
  "weekly",
  "biweekly",
  "monthly",
  "quarterly",
  "yearly",
] as const;

const emptyRecurringLineItem = (): RecurringLineItemForm => ({
  description: "",
  quantity: "1",
  unitPrice: "0",
});

const emptyRecurringForm = (): RecurringForm => ({
  clientId: "",
  title: "",
  frequency: "monthly",
  nextRunDate: new Date().toISOString().slice(0, 10),
  taxRate: "0",
  notes: "",
  autoSend: false,
  // Start with one row rather than an empty list: the form requires at least
  // one, and an empty table with an "Add line" button reads as optional.
  lineItems: [emptyRecurringLineItem()],
});

export interface RecurringRow {
  id: string;
  clientId: string;
  title: string;
  frequency: string;
  nextRunDate: string;
  endDate?: string | null;
  isActive?: boolean | number;
  autoSend?: boolean | number;
  lastGenerated?: string | null;
  taxRate?: number | string;
}

export { formatCurrency, num };

export type RecurringSortKey =
  | "title"
  | "clientName"
  | "frequency"
  | "nextRunDate"
  | "endDate"
  | "state";

/**
 * Sort keys that read best latest/biggest-first on first click. `nextRunDate`
 * deliberately stays asc-first — soonest-due on top is today's default and the
 * useful read. `state` sorts a rank (due > active > paused), so desc puts what
 * needs attention first.
 */
const DESC_FIRST: ReadonlySet<RecurringSortKey> = new Set(["endDate", "state"]);

const todayIso = () => new Date().toISOString().slice(0, 10);

/** Route ViewStore for Billing → Recurring. Derives over the ServiceStore cache. */
export class RecurringStore {
  rootStore: RootStore;

  search = "";
  activeOnly = true;

  // Column sort (applied client-side over the filtered set, like the filters)
  sortKey: RecurringSortKey = "nextRunDate";
  sortDir: SortDir = "asc";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get service(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "recurring-invoices"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get clientsService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "clients"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  get loading(): boolean {
    return this.service?.isFindPending === true;
  }

  setSearch(value: string) {
    this.search = value;
  }

  setActiveOnly(value: boolean) {
    this.activeOnly = value;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (see DESC_FIRST; text A→Z, next run soonest-first). */
  setSort(key: RecurringSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  clientName(clientId: string): string {
    const rows = (this.clientsService?.entities ?? []) as unknown as Array<{
      id: string;
      name?: string;
    }>;
    return rows.find((c) => String(c.id) === String(clientId))?.name ?? "Unknown client";
  }

  get allSchedules(): RecurringRow[] {
    const rows = (this.service?.entities ?? []) as unknown as RecurringRow[];
    return rows
      .filter((r) => r?.id != null)
      .slice()
      .sort((a, b) => String(a.nextRunDate).localeCompare(String(b.nextRunDate)));
  }

  get schedules(): RecurringRow[] {
    const term = this.search.trim().toLowerCase();
    return this.allSchedules.filter((row) => {
      if (this.activeOnly && !this.isActive(row)) return false;
      if (!term) return true;
      return [row.title, this.clientName(row.clientId), row.frequency]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }

  /**
   * The rows both layouts render: filters, then the active column sort. Nulls
   * (e.g. no end date) sort last in either direction.
   */
  get sortedSchedules(): RecurringRow[] {
    const key = this.sortKey;
    const valueOf = (row: RecurringRow): SortValue => {
      switch (key) {
        case "title":
          return textValue(row.title);
        case "clientName":
          return textValue(this.clientName(row.clientId));
        case "frequency":
          return textValue(row.frequency);
        case "nextRunDate":
          return dateValue(row.nextRunDate);
        case "endDate":
          return dateValue(row.endDate);
        case "state":
          // Rank so a desc sort reads Due, then Active, then Paused.
          return this.isDue(row) ? 2 : this.isActive(row) ? 1 : 0;
      }
    };
    return sortRows(this.schedules, valueOf, this.sortDir);
  }

  /** BIT columns arrive as 0/1 from tedious, so never compare against `true`. */
  isActive(row: RecurringRow): boolean {
    return Boolean(row.isActive);
  }

  /** A schedule whose next run has already passed is owed invoices. */
  isDue(row: RecurringRow): boolean {
    return this.isActive(row) && String(row.nextRunDate).slice(0, 10) <= todayIso();
  }

  get dueCount(): number {
    return this.allSchedules.filter((r) => this.isDue(r)).length;
  }

  /** Why the list itself did not load — the Body says so instead of an
   *  empty "No … yet." Null after a successful load. */
  loadError: string | null = null;
  /** The clients lookup failed: rows still render (names fall back to ids),
   *  but the client filter and the form's picker would be silently empty. */
  clientsError: string | null = null;

  /**
   * Re-read the list and the clients lookup. NEVER rejects: it is also the
   * reload after a successful write, and a failed reload must not turn that
   * write into "save failed". Failures land on `loadError` / `clientsError`.
   *
   * findOrThrow: the ServiceStore resolves null on a failed find (throwOnError
   * defaults to false), which rendered as "No recurring schedules yet."
   */
  async refresh(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.loadError = null;
      this.clientsError = null;
    });
    const [list, clients] = await Promise.allSettled([
      findOrThrow(this.service, { query: { $limit: 500, $sort: { nextRunDate: 1 } } }),
      findOrThrow(this.clientsService, { query: { $limit: 500 } }),
    ]);
    // allSettled waits for BOTH: a leg that failed before a sign-out is still
    // the previous person's failure when the other settles after it.
    if (ended()) return;
    runInAction(() => {
      if (list.status === "rejected" && !isSessionEnded(list.reason)) {
        this.loadError = loadErrorMessage(list.reason, "Failed to load recurring schedules");
      }
      if (clients.status === "rejected" && !isSessionEnded(clients.reason)) {
        this.clientsError = loadErrorMessage(clients.reason, "Failed to load clients");
      }
    });
    if (list.status === "rejected") console.error("billing: failed to load recurring schedules", list.reason);
    if (clients.status === "rejected") console.error("billing: failed to load clients", clients.reason);
  }

  // ── Create ────────────────────────────────────────────────────────
  //
  // Item 50 · This page had no create affordance at all. Writes go through the
  // RAW feathers service rather than the ServiceStore for the same reason
  // Invoices does: `ServiceStore.create` swallows server errors and resolves
  // undefined, which on a schedule that mints invoices would report a rejected
  // write as saved.

  form: RecurringForm = emptyRecurringForm();
  saving = false;
  formError: string | null = null;

  get clientOptions(): Array<{ id: string; name: string }> {
    const rows = (this.clientsService?.entities ?? []) as unknown as Array<{
      id: string;
      name?: string;
    }>;
    return rows
      .filter((c) => c?.id != null)
      .map((c) => ({ id: String(c.id), name: c.name ?? String(c.id) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  startCreate() {
    this.form = emptyRecurringForm();
    this.formError = null;
  }

  setFormField<K extends keyof RecurringForm>(key: K, value: RecurringForm[K]) {
    this.form = { ...this.form, [key]: value };
    this.formError = null;
  }

  addLineItem() {
    this.form = {
      ...this.form,
      lineItems: [...this.form.lineItems, emptyRecurringLineItem()],
    };
  }

  setLineItem(
    index: number,
    field: keyof RecurringLineItemForm,
    value: string
  ) {
    this.form = {
      ...this.form,
      lineItems: this.form.lineItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    };
    this.formError = null;
  }

  removeLineItem(index: number) {
    this.form = {
      ...this.form,
      lineItems: this.form.lineItems.filter((_, i) => i !== index),
    };
  }

  /** Rows with something in the description — the ones the API will keep. */
  get validLineItems(): RecurringLineItemForm[] {
    return this.form.lineItems.filter(
      (item) => item.description.trim().length > 0
    );
  }

  /** What the schedule will bill per period, for the form's running total. */
  get formSubtotal(): number {
    return this.validLineItems.reduce(
      (sum, item) => sum + num(item.quantity, 1) * num(item.unitPrice, 0),
      0
    );
  }

  get isFormValid(): boolean {
    return (
      !!this.form.clientId &&
      this.form.title.trim().length > 0 &&
      !!this.form.frequency &&
      !!this.form.nextRunDate &&
      // At least one billable row. Without this the form's only possible
      // output is a schedule that bills nothing, with no way to fix it later.
      this.validLineItems.length > 0 &&
      !this.saving
    );
  }

  async save(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError =
        this.validLineItems.length === 0
          ? "Add at least one line item — it is the template every generated invoice bills from."
          : "Pick a client, a title, a frequency and a first run date.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    try {
      const api = this.rootStore.app as
        | { service: (p: string) => { create(data: object): Promise<unknown> } }
        | undefined;
      if (!api) throw new Error("Not connected to the API — reload and try again.");
      await api.service("recurring-invoices").create({
        clientId: this.form.clientId,
        title: this.form.title.trim(),
        frequency: this.form.frequency,
        nextRunDate: this.form.nextRunDate,
        taxRate: num(this.form.taxRate),
        notes: this.form.notes.trim() || null,
        autoSend: this.form.autoSend,
        isActive: true,
        // Lifted off the payload server-side by `extractLineItems` and written
        // to `recurring_line_items` — the template the schedule bills from.
        lineItems: this.validLineItems.map((item) => ({
          description: item.description.trim(),
          quantity: num(item.quantity, 1),
          unitPrice: num(item.unitPrice, 0),
        })),
      });
      await this.refresh();
      return true;
    } catch (err) {
      if (!isSessionEnded(err)) {
        this.formError =
          err instanceof Error ? err.message : "Failed to create the schedule.";
      }
      return false;
    } finally {
      this.saving = false;
    }
  }

  /** Route entry. `refresh` never rejects; a failure shows on the page. */
  async initialize(): Promise<void> {
    await this.refresh();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.search = "";
    this.activeOnly = true;
    this.sortKey = "nextRunDate";
    this.sortDir = "asc";
    this.loadError = null;
    this.clientsError = null;
    this.form = emptyRecurringForm();
    this.saving = false;
    this.formError = null;
  }
}

export default RecurringStore;
